import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, router } from '@inertiajs/react'
import {
  CreditCard,
  ExternalLink,
  ImageOff,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
} from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import CreatorShell from '@/components/explore/CreatorShell'
import { PUBLIC_CONTAINER } from '@/components/public/publicShell'
import { cn } from '@/lib/utils'
import type { CreatorPage } from '@/types/creator'
import type { CityOption } from '@/types/publicSchool'

interface Props {
  cities: CityOption[]
  pages: CreatorPage[]
}

const PLAN_LABEL: Record<CreatorPage['plan'], string> = {
  mensal: 'Mensal',
  semestral: 'Semestral',
  anual: 'Anual',
}

const formatPrice = (value: number) =>
  value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  })

/**
 * Botão principal da tela. Fica numa constante porque aparece duas vezes — no
 * cabeçalho e no vazio — e um azul diferente em cada um foi exatamente o que
 * aconteceu enquanto eram dois literais (`bg-lp-blue`, que é o azul da LANDING,
 * e não o `primary` da marca).
 */
const BOTAO_PRIMARIO =
  'inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90'

/** ~2 minutos de espera (40 × 3s) antes de admitir que a geração travou. */
const POLL_INTERVAL_MS = 3000
const POLL_MAX = 40

/**
 * Minhas Páginas — a única tela que o criador logado tem. Ele só faz uma
 * coisa aqui: criar e gerenciar páginas do BeWave Explore.
 *
 * Enquanto alguma página está `generating`, faz polling (só do prop `pages`)
 * até todas saírem desse estado — é assim que o card troca de "Gerando…" para
 * "Publicada" sem o criador precisar recarregar a tela na mão.
 */
export default function MinhasPaginas({ cities, pages }: Props) {
  const hasGenerating = pages.some((page) => page.status === 'generating')
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [tentativas, setTentativas] = useState(0)

  // Desiste depois de POLL_MAX voltas.
  //
  // A geração leva segundos. Se passou disso, o job não está rodando — worker
  // do Sidekiq parado é o caso clássico — e insistir não resolve: só mantém a
  // aba pedindo /minhas-paginas de 3 em 3 segundos para sempre (dá para ver no
  // log do servidor) enquanto o criador olha um "Gerando…" que nunca termina.
  //
  // Parar e DIZER é melhor: o card passa a explicar que travou e oferece o
  // recarregar, em vez de girar em silêncio.
  const desistiu = tentativas >= POLL_MAX

  useEffect(() => {
    if (!hasGenerating || desistiu) return

    intervalRef.current = setInterval(() => {
      setTentativas((n) => n + 1)
      router.reload({ only: ['pages'] })
    }, POLL_INTERVAL_MS)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [hasGenerating, desistiu])

  // Página nova entrando em geração zera o contador — senão a segunda página
  // criada na mesma sessão herdaria as tentativas gastas com a primeira e
  // nasceria "travada".
  useEffect(() => {
    if (!hasGenerating) setTentativas(0)
  }, [hasGenerating])

  // O retorno do checkout do Mercado Pago (?pagamento=sucesso|pendente|falha).
  //
  // O webhook confirma sozinho, mas o navegador chega ANTES dele — e em
  // desenvolvimento não há túnel nenhum. A sincronização ativa pergunta ao
  // servidor (que pergunta ao MP) e recarrega as páginas: o card vira "no ar"
  // segundos depois do pagamento, sem F5.
  const [retorno, setRetorno] = useState<string | null>(null)

  useEffect(() => {
    const resultado = new URLSearchParams(window.location.search).get(
      'pagamento',
    )
    if (!resultado) return
    setRetorno(resultado)
    if (resultado !== 'sucesso') return

    const pendentes = pages.filter(
      (p) => p.payment_status === 'payment_pending',
    )
    Promise.all(
      pendentes.map((p) =>
        fetch(p.payment_href, {
          headers: { Accept: 'application/json' },
        }).catch(() => null),
      ),
    ).then(() => router.reload({ only: ['pages'] }))
    // Só na chegada — o parâmetro não muda sem nova navegação de fora.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <CreatorShell title="Minhas páginas — BeWave Explore" cities={cities}>
      {/* A MESMA faixa do header (PUBLIC_CONTAINER), e não uma coluna estreita
          centralizada: com `max-w-3xl mx-auto` o conteúdo nascia deslocado da
          logo e o vazio ficava boiando no meio da tela. */}
      <div className={cn(PUBLIC_CONTAINER, 'flex-1 py-10 sm:py-12')}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl">
              Minhas páginas
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Crie e gerencie suas páginas no BeWave Explore.
            </p>
          </div>

          <Link href="/nova-pagina" className={BOTAO_PRIMARIO}>
            <Plus aria-hidden="true" className="size-4" />
            Criar página
          </Link>
        </div>

        {retorno === 'sucesso' && (
          <p className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            Pagamento aprovado! Sua página entra no ar assim que a confirmação
            chegar — segundos, normalmente.
          </p>
        )}
        {retorno === 'falha' && (
          <p className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            O pagamento não foi concluído. Quando quiser, é só tentar de novo
            pelo botão do card.
          </p>
        )}

        {pages.length === 0 ? (
          <EmptyState />
        ) : (
          <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {pages.map((page) => (
              <PageCard key={page.id} page={page} travou={desistiu} />
            ))}
          </ul>
        )}
      </div>
    </CreatorShell>
  )
}

/**
 * O vazio.
 *
 * Alinhado à esquerda como o resto da tela, e com largura de leitura: uma caixa
 * centralizada na faixa inteira deixava o texto solto no meio de 1760px.
 */
function EmptyState() {
  return (
    <div className="mt-8 max-w-xl rounded-2xl border border-dashed border-gray-300 px-6 py-12">
      <div className="flex size-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
        <Sparkles aria-hidden="true" className="size-7" strokeWidth={1.75} />
      </div>
      <h2 className="mt-6 text-xl font-bold text-gray-900">Crie sua página</h2>
      <p className="mt-3 text-sm leading-relaxed text-gray-600">
        Em poucos minutos, nossa IA vai montar uma página profissional para o
        seu negócio. Você só precisa responder algumas perguntas rápidas.
      </p>
      <Link href="/nova-pagina" className={cn(BOTAO_PRIMARIO, 'mt-6')}>
        <Plus aria-hidden="true" className="size-4" />
        Criar página
      </Link>
    </div>
  )
}

function StatusBadge({
  page,
  travou = false,
}: {
  page: CreatorPage
  /** A espera estourou o teto — ver POLL_MAX. */
  travou?: boolean
}) {
  if (page.status === 'generating') {
    if (travou) {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700">
          Demorando demais
        </span>
      )
    }

    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
        <span className="size-1.5 animate-pulse rounded-full bg-amber-500" />
        Gerando…
      </span>
    )
  }

  if (page.status === 'published') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
        Publicada
      </span>
    )
  }

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">
      {page.status === 'draft' ? 'Rascunho' : 'Desativada'}
    </span>
  )
}

function PageCard({ page, travou }: { page: CreatorPage; travou: boolean }) {
  const [confirmando, setConfirmando] = useState(false)
  const [pagando, setPagando] = useState(false)
  const [erroDePagamento, setErroDePagamento] = useState<string | null>(null)

  // Abre o Checkout Pro: o servidor cria a preference (valor SEMPRE do
  // snapshot dele) e devolve a URL hospedada do MP — PIX, cartão ou boleto.
  const pagar = useCallback(async () => {
    if (pagando) return
    setPagando(true)
    setErroDePagamento(null)
    try {
      const resposta = await fetch(page.payment_href, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token':
            document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute('content') ?? '',
        },
      })
      const corpo = await resposta.json()
      if (corpo.url) {
        window.location.assign(corpo.url)
        return // a página vai embora; não desliga o "pagando" para o botão não piscar
      }
      setErroDePagamento(corpo.error ?? 'Não foi possível abrir o pagamento.')
    } catch {
      setErroDePagamento('Sem conexão agora. Tenta de novo em instantes?')
    }
    setPagando(false)
  }, [page.payment_href, pagando])

  return (
    <li className="flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white">
      {/* Capa: a arte do serviço da página. Página de criador não tem foto
          própria, e uma ilustração do que o negócio faz diz mais do que a mesma
          imagem genérica para todo mundo. Sem arte, o mesmo placeholder de onda
          que o diretório usa em card sem foto — assim as duas telas mostram o
          "sem imagem" do mesmo jeito. */}
      {/* Capa. A página de criador ainda NÃO tem foto própria, então o normal
          aqui é o vazio — e o vazio é dito com todas as letras, em cinza neutro.
          Chegou a mostrar a arte do serviço (o card do Figma): o resultado era
          uma ilustração de catálogo posando de foto do negócio, e todas as
          páginas do mesmo serviço com a mesma "foto". */}
      <div className="relative aspect-[16/10] shrink-0 bg-gray-50">
        {page.image_path ? (
          <img
            src={page.image_path}
            alt=""
            loading="lazy"
            className="size-full object-cover"
          />
        ) : (
          <div className="flex size-full flex-col items-center justify-center gap-1.5 text-gray-400">
            <ImageOff className="size-6" aria-hidden="true" strokeWidth={1.5} />
            <span className="text-xs font-medium">Sem imagem</span>
          </div>
        )}

        <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
          <StatusBadge page={page} travou={travou} />
          {page.payment_status === 'payment_pending' && (
            <span className="inline-flex items-center rounded-full bg-white/90 px-2.5 py-1 text-xs font-medium text-gray-600">
              Pagamento pendente
            </span>
          )}
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col p-4">
        <h3 className="truncate text-base font-semibold text-gray-900">
          {page.name}
        </h3>

        {/* Depois de ~2 minutos a geração não vai terminar sozinha. Dizer isso
            e oferecer o recarregar é melhor do que um "Gerando…" girando para
            sempre — o texto do seu conteúdo não se perde, só o da IA que ainda
            não veio. */}
        {page.status === 'generating' && travou && (
          <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
            <p className="text-xs leading-relaxed text-red-800">
              A geração está demorando mais que o normal. Seus dados estão
              salvos — recarregue em instantes ou fale com a gente.
            </p>
            <button
              type="button"
              onClick={() => router.reload({ only: ['pages'] })}
              className="mt-1.5 text-xs font-semibold text-red-800 underline underline-offset-2"
            >
              Verificar de novo
            </button>
          </div>
        )}

        {page.city && (
          <p className="mt-0.5 truncate text-sm text-gray-500">
            {page.city}
            {page.state && `/${page.state}`}
          </p>
        )}

        {page.categories.length > 0 && (
          <p className="mt-2 flex flex-wrap gap-1.5 text-xs text-gray-500">
            {page.categories.map((label) => (
              <span
                key={label}
                className="rounded-full bg-gray-100 px-2 py-0.5"
              >
                {label}
              </span>
            ))}
          </p>
        )}

        <p className="mt-2 text-sm text-gray-600">
          Plano {PLAN_LABEL[page.plan]} · {formatPrice(page.monthly_total)}/mês
        </p>

        {/* Pendente, a AÇÃO da página é pagar: botão primário, com o valor —
            é ele que põe a página no ar. */}
        {page.payment_status === 'payment_pending' && (
          <div className="pt-4">
            <button
              type="button"
              onClick={pagar}
              disabled={pagando}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              <CreditCard aria-hidden="true" className="size-4" />
              {pagando
                ? 'Abrindo pagamento…'
                : `Pagar assinatura · ${formatPrice(page.period_total)}`}
            </button>
            {erroDePagamento && (
              <p className="mt-1.5 text-xs text-red-600">{erroDePagamento}</p>
            )}
          </div>
        )}

        {/* `mt-auto`: as ações encostam no fim do card, e a fileira fica com os
            botões alinhados mesmo com nomes e categorias de alturas diferentes. */}
        <div className="mt-auto flex items-center gap-2 pt-4">
          {/* Rascunho volta para o WIZARD, no passo onde parou; página pronta
              vai para a tela de edição. São formulários diferentes — o de
              edição nem tem os campos que faltam num rascunho, e o servidor
              recusa abrir rascunho por lá. */}
          <Link
            href={page.resume_href ?? page.edit_href}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-800 transition-colors hover:bg-gray-50"
          >
            <Pencil aria-hidden="true" className="size-3.5" />
            {page.resume_href ? 'Continuar' : 'Editar'}
          </Link>

          {page.status === 'published' &&
            (page.public_live && page.href ? (
              <a
                href={page.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Ver página de ${page.name}`}
                title="Ver página"
                className="inline-flex size-9 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 transition-colors hover:bg-gray-50"
              >
                <ExternalLink aria-hidden="true" className="size-4" />
              </a>
            ) : (
              // Publicada sem estar no ar: o gate é o pagamento.
              <span className="shrink-0 text-xs text-gray-400">
                {page.payment_status === 'payment_pending'
                  ? 'Entra no ar após o pagamento'
                  : 'No ar em breve'}
              </span>
            ))}

          <button
            type="button"
            onClick={() => setConfirmando(true)}
            aria-label={`Excluir ${page.name}`}
            title="Excluir"
            className="inline-flex size-9 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 aria-hidden="true" className="size-4" />
          </button>
        </div>
      </div>

      {/* Diálogo do produto, não o `window.confirm` do navegador: o nativo é
          bloqueante, não dá para estilizar, e alguns navegadores oferecem
          "impedir que esta página crie mais diálogos" — desligando de vez a
          única confirmação de uma ação irreversível. */}
      <AlertDialog open={confirmando} onOpenChange={setConfirmando}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir “{page.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              A página sai do ar e do BeWave Explore. Não dá para desfazer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => router.delete(`/minhas-paginas/${page.id}`)}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Excluir página
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  )
}
